import React, { useState } from "react";
import { motion } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import { footerData } from "../data";
import { Heart, Send } from "lucide-react";
import LogoAppe from "../img/header/logoAppe.jpg";

const DONATE_URL =
  "https://www.helloasso.com/associations/association-des-professionnels-de-la-petite-enfance/collectes/aider-l-ecole-primaire-a-sarh-au-tchad-fonctionnement-achat-de-tables-bancs";

const Footer = () => {
  const { description, contacts, links, newsletter } = footerData;
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
    }
  };

  return (
    <footer className="bg-dark text-white">
      {/* Main footer */}
      <div className="container mx-auto py-16 lg:py-20">
        <motion.div
          variants={staggerContainer(0.1, 0.05)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12"
        >
          {/* Brand column */}
          <motion.div variants={fadeIn("up")} className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={LogoAppe}
                alt="APPE Tchad"
                className="w-12 h-12 rounded-full object-cover border-2 border-secondary/30"
              />
              <div>
                <div className="font-display font-bold text-white text-sm">APPE Tchad</div>
                <div className="text-white/50 text-xs">Depuis 2004</div>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">{description}</p>
            <a
              href={DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-secondary hover:bg-secondary-dark text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors shadow-md"
            >
              <Heart size={14} fill="currentColor" />
              Faire un don
            </a>
          </motion.div>

          {/* Contact column */}
          <motion.div variants={fadeIn("up", 0.05)}>
            <div className="font-display font-semibold text-sm uppercase tracking-widest mb-6 text-white/80">
              Nos contacts
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-secondary flex-shrink-0 mt-0.5">{contacts.address.icon}</span>
                <span className="text-white/60 text-sm leading-relaxed">{contacts.address.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-secondary flex-shrink-0">{contacts.phone.icon}</span>
                <a href={`tel:${contacts.phone.number.replace(/\s/g, "")}`} className="text-white/60 hover:text-white text-sm transition-colors">
                  {contacts.phone.number}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-secondary flex-shrink-0">{contacts.email.icon}</span>
                <a href={`mailto:${contacts.email.address}`} className="text-white/60 hover:text-white text-sm transition-colors break-all">
                  {contacts.email.address}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Links column */}
          <motion.div variants={fadeIn("up", 0.1)}>
            <div className="font-display font-semibold text-sm uppercase tracking-widest mb-6 text-white/80">
              Liens rapides
            </div>
            <ul className="space-y-2.5">
              {links.map((link, i) => (
                <li key={i}>
                  <a
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors hover:translate-x-0.5 inline-block"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Newsletter column */}
          <motion.div variants={fadeIn("up", 0.15)}>
            <div className="font-display font-semibold text-sm uppercase tracking-widest mb-6 text-white/80">
              Newsletter
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Restez informés de nos projets, événements et avancées sur le terrain.
            </p>
            {subscribed ? (
              <div className="bg-accent/20 border border-accent/30 rounded-xl px-4 py-3 text-accent text-sm font-medium">
                Merci pour votre inscription !
              </div>
            ) : (
              <form onSubmit={handleNewsletter} className="flex gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={newsletter.placeholder}
                  className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/40 text-sm rounded-xl px-4 py-3 outline-none focus:border-secondary/60 transition-colors"
                />
                <button
                  type="submit"
                  className="w-11 h-11 rounded-xl bg-secondary hover:bg-secondary-dark flex items-center justify-center flex-shrink-0 transition-colors shadow-md"
                >
                  <Send size={14} />
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()} APPE Tchad — Association des Professionnels de la Petite Enfance. Tous droits réservés.
          </p>
          <p className="text-white/40 text-xs flex items-center gap-1">
            Fait avec <Heart size={10} fill="currentColor" className="text-secondary" /> pour les enfants du Tchad
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
